import React, { useState, useRef, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { useAuth } from 'shared/context/AuthContext';
import * as usersApi from 'shared/api/auth/usersApi';
import * as emailApi from 'shared/api/auth/emailApi';

const PersonalInformation = ({ onUnsavedChanges, hasUnsavedChanges }) => {
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  
  // Email change state
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [emailChangeStep, setEmailChangeStep] = useState('request'); // 'request' | 'verify'
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  
  // Form state - только поля доступные в API
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    username: '',
    avatarUrl: ''
  });

  const [originalData, setOriginalData] = useState({ ...formData });

  // Initialize form data from user context
  useEffect(() => {
    if (user) {
      const userData = {
        name: user.name || '',
        surname: user.surname || '',
        username: user.username || '',
        avatarUrl: user.avatarUrl || ''
      };
      setFormData(userData);
      setOriginalData(userData);
      if (user.avatarUrl) {
        setProfileImage(user.avatarUrl);
      }
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    const hasChanges = JSON.stringify(newFormData) !== JSON.stringify(originalData);
    onUnsavedChanges(hasChanges);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setSaveError('Размер файла не должен превышать 5 МБ');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
        handleInputChange('avatarUrl', e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess('');
    
    try {
      const updatedUser = await usersApi.updateMe({
        name: formData.name,
        surname: formData.surname,
        username: formData.username,
        avatarUrl: formData.avatarUrl || null
      });
      
      // Update auth context with new user data
      updateUser(updatedUser);
      
      setOriginalData({ ...formData });
      onUnsavedChanges(false);
      setIsEditing(false);
      setSaveSuccess('Данные успешно сохранены');
      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
      if (err.status === 400 && err.data?.errors?.username) {
        setSaveError('Такой username уже занят');
      } else {
        setSaveError(err.message || 'Не удалось сохранить изменения');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({ ...originalData });
    setProfileImage(originalData.avatarUrl || null);
    onUnsavedChanges(false);
    setIsEditing(false);
    setSaveError('');
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSaveError('');
    setSaveSuccess('');
  };

  // Email change handlers
  const handleRequestEmailChange = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      setEmailError('Введите корректный email адрес');
      return;
    }
    
    setIsChangingEmail(true);
    setEmailError('');
    
    try {
      await emailApi.requestEmailChange({ newEmail });
      setEmailChangeStep('verify');
      setEmailSuccess('Код подтверждения отправлен на новый email');
    } catch (err) {
      console.error('Failed to request email change:', err);
      setEmailError(err.message || 'Не удалось отправить код подтверждения');
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleVerifyEmailChange = async () => {
    if (!verificationCode || verificationCode.length < 4) {
      setEmailError('Введите код подтверждения');
      return;
    }
    
    setIsChangingEmail(true);
    setEmailError('');
    
    try {
      const result = await emailApi.verifyEmailChange({ verificationCode });
      setEmailSuccess(`Email успешно изменен на ${result.new_email}`);
      setShowEmailChange(false);
      setEmailChangeStep('request');
      setNewEmail('');
      setVerificationCode('');
      // Refresh user data
      const updatedUser = await usersApi.getMe();
      updateUser(updatedUser);
    } catch (err) {
      console.error('Failed to verify email change:', err);
      if (err.status === 400) {
        setEmailError('Неверный или истекший код подтверждения');
      } else {
        setEmailError(err.message || 'Не удалось подтвердить изменение email');
      }
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleCancelEmailChange = () => {
    setShowEmailChange(false);
    setEmailChangeStep('request');
    setNewEmail('');
    setVerificationCode('');
    setEmailError('');
    setEmailSuccess('');
  };

  // Get initials from name
  const getInitials = () => {
    const name = formData.name || '';
    const surname = formData.surname || '';
    if (name && surname) {
      return `${name[0]}${surname[0]}`.toUpperCase();
    }
    if (name) return name.slice(0, 2).toUpperCase();
    if (user?.username) return user.username.slice(0, 2).toUpperCase();
    return 'U';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">
            Личная информация
          </h2>
          <p className="text-text-secondary mt-1">
            Управляйте своими личными данными
          </p>
        </div>
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="
              flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md
              hover:bg-primary-700 transition-colors duration-150 hover-lift
              focus:outline-none focus:ring-2 focus:ring-primary/20
            "
          >
            <Icon name="Edit" size={16} />
            Редактировать
          </button>
        )}
      </div>

      {/* Success/Error Messages */}
      {saveSuccess && (
        <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-md flex items-center gap-2">
          <Icon name="CheckCircle" size={18} className="text-success" />
          <p className="text-sm text-success">{saveSuccess}</p>
        </div>
      )}
      
      {saveError && (
        <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={18} className="text-error" />
            <p className="text-sm text-error">{saveError}</p>
          </div>
          <button onClick={() => setSaveError('')} className="text-error hover:text-error-700">
            <Icon name="X" size={16} />
          </button>
        </div>
      )}

      {/* Profile Picture Section */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-text-primary mb-4">
          Фотография профиля
        </h3>
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center overflow-hidden">
              {profileImage ? (
                <Image 
                  src={profileImage} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-2xl font-semibold text-white">{getInitials()}</span>
              )}
            </div>
            {isEditing && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="
                  absolute -bottom-2 -right-2 w-8 h-8 bg-accent text-white rounded-full
                  flex items-center justify-center hover:bg-accent-700 transition-colors duration-150
                  focus:outline-none focus:ring-2 focus:ring-accent/20
                "
              >
                <Icon name="Camera" size={14} />
              </button>
            )}
          </div>
          <div>
            <p className="text-sm text-text-secondary mb-2">
              Рекомендуемый размер: 400x400 пикселей
            </p>
            <p className="text-sm text-text-secondary mb-3">
              Максимальный размер файла: 5 МБ
            </p>
            {isEditing && (
              <div className="flex gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="
                    px-3 py-1.5 text-sm border border-secondary-300 rounded-md
                    hover:bg-secondary-50 transition-colors duration-150
                    focus:outline-none focus:ring-2 focus:ring-primary/20
                  "
                >
                  Загрузить фото
                </button>
                {profileImage && (
                  <button
                    onClick={() => {
                      setProfileImage(null);
                      handleInputChange('avatarUrl', '');
                    }}
                    className="
                      px-3 py-1.5 text-sm text-error border border-error-300 rounded-md
                      hover:bg-error-50 transition-colors duration-150
                      focus:outline-none focus:ring-2 focus:ring-error/20
                    "
                  >
                    Удалить
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
      </div>

      {/* Personal Information Form */}
      <div className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Имя *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={!isEditing}
              placeholder="Введите имя"
              className={`
                w-full px-3 py-2 border rounded-md transition-colors duration-150
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                ${isEditing 
                  ? 'border-secondary-300 bg-surface' 
                  : 'border-secondary-200 bg-secondary-50 text-text-secondary'
                }
              `}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Фамилия *
            </label>
            <input
              type="text"
              value={formData.surname}
              onChange={(e) => handleInputChange('surname', e.target.value)}
              disabled={!isEditing}
              placeholder="Введите фамилию"
              className={`
                w-full px-3 py-2 border rounded-md transition-colors duration-150
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                ${isEditing 
                  ? 'border-secondary-300 bg-surface' 
                  : 'border-secondary-200 bg-secondary-50 text-text-secondary'
                }
              `}
            />
          </div>
        </div>

        {/* Username */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Имя пользователя (username) *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">@</span>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              disabled={!isEditing}
              placeholder="username"
              className={`
                w-full pl-8 pr-3 py-2 border rounded-md transition-colors duration-150
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                ${isEditing 
                  ? 'border-secondary-300 bg-surface' 
                  : 'border-secondary-200 bg-secondary-50 text-text-secondary'
                }
              `}
            />
          </div>
          {isEditing && (
            <p className="text-xs text-text-secondary mt-1">
              Только латинские буквы, цифры и подчеркивания
            </p>
          )}
        </div>

        {/* Email Section */}
        <div className="pt-4 border-t border-secondary-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-text-primary">
                Email адрес
              </h3>
              <p className="text-sm text-text-secondary">
                Используется для входа в систему и уведомлений
              </p>
            </div>
            {!showEmailChange && (
              <button
                onClick={() => setShowEmailChange(true)}
                className="
                  flex items-center gap-2 px-3 py-1.5 text-sm border border-secondary-300 rounded-md
                  hover:bg-secondary-50 transition-colors duration-150
                  focus:outline-none focus:ring-2 focus:ring-primary/20
                "
              >
                <Icon name="Mail" size={14} />
                Изменить email
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-md">
            <Icon name="Mail" size={18} className="text-text-secondary" />
            <span className="text-text-primary">{user?.email || 'Не указан'}</span>
            {user?.isEmailVerified && (
              <span className="flex items-center gap-1 text-xs text-success">
                <Icon name="CheckCircle" size={12} />
                Подтвержден
              </span>
            )}
          </div>

          {/* Email Change Form */}
          {showEmailChange && (
            <div className="mt-4 p-4 border border-secondary-200 rounded-md">
              {emailSuccess && (
                <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-md">
                  <p className="text-sm text-success">{emailSuccess}</p>
                </div>
              )}
              
              {emailError && (
                <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-md flex items-center justify-between">
                  <p className="text-sm text-error">{emailError}</p>
                  <button onClick={() => setEmailError('')} className="text-error">
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}

              {emailChangeStep === 'request' ? (
                <>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Новый email адрес
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="new@example.com"
                    className="
                      w-full px-3 py-2 border border-secondary-300 rounded-md
                      focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                    "
                  />
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleRequestEmailChange}
                      disabled={isChangingEmail}
                      className="
                        flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md
                        hover:bg-primary-700 transition-colors duration-150
                        disabled:opacity-50 disabled:cursor-not-allowed
                      "
                    >
                      {isChangingEmail && <Icon name="Loader2" size={16} className="animate-spin" />}
                      Отправить код
                    </button>
                    <button
                      onClick={handleCancelEmailChange}
                      className="
                        px-4 py-2 border border-secondary-300 rounded-md
                        hover:bg-secondary-50 transition-colors duration-150
                      "
                    >
                      Отмена
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Код подтверждения
                  </label>
                  <p className="text-sm text-text-secondary mb-3">
                    Введите код, отправленный на {newEmail}
                  </p>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="123456"
                    maxLength={6}
                    className="
                      w-full px-3 py-2 border border-secondary-300 rounded-md text-center text-lg tracking-widest
                      focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                    "
                  />
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleVerifyEmailChange}
                      disabled={isChangingEmail}
                      className="
                        flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md
                        hover:bg-primary-700 transition-colors duration-150
                        disabled:opacity-50 disabled:cursor-not-allowed
                      "
                    >
                      {isChangingEmail && <Icon name="Loader2" size={16} className="animate-spin" />}
                      Подтвердить
                    </button>
                    <button
                      onClick={() => setEmailChangeStep('request')}
                      className="
                        px-4 py-2 border border-secondary-300 rounded-md
                        hover:bg-secondary-50 transition-colors duration-150
                      "
                    >
                      Назад
                    </button>
                    <button
                      onClick={handleCancelEmailChange}
                      className="
                        px-4 py-2 text-error hover:bg-error-50 rounded-md transition-colors duration-150
                      "
                    >
                      Отмена
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Role Info (read-only) */}
        <div className="pt-4 border-t border-secondary-200">
          <h3 className="text-lg font-medium text-text-primary mb-4">
            Информация об аккаунте
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 bg-secondary-50 rounded-md">
              <p className="text-xs text-text-secondary mb-1">Роль</p>
              <p className="text-text-primary font-medium flex items-center gap-2">
                <Icon name="Shield" size={16} className="text-primary" />
                {user?.role === 'admin' ? 'Администратор' : 'Пользователь'}
              </p>
            </div>
            <div className="p-3 bg-secondary-50 rounded-md">
              <p className="text-xs text-text-secondary mb-1">Статус</p>
              <p className="text-text-primary font-medium flex items-center gap-2">
                <Icon 
                  name={user?.isActive ? 'CheckCircle' : 'XCircle'} 
                  size={16} 
                  className={user?.isActive ? 'text-success' : 'text-error'} 
                />
                {user?.isActive ? 'Активен' : 'Деактивирован'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {isEditing && (
        <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-secondary-200">
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="
              px-4 py-2 border border-secondary-300 text-text-primary rounded-md
              hover:bg-secondary-50 transition-colors duration-150 hover-lift
              focus:outline-none focus:ring-2 focus:ring-primary/20
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            Отменить
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !formData.name || !formData.surname || !formData.username}
            className="
              px-4 py-2 bg-primary text-white rounded-md
              hover:bg-primary-700 transition-colors duration-150 hover-lift
              focus:outline-none focus:ring-2 focus:ring-primary/20
              flex items-center gap-2
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {isSaving ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                Сохранение...
              </>
            ) : (
              <>
                <Icon name="Save" size={16} />
                Сохранить изменения
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default PersonalInformation;
